import { Component, effect, Inject, OnDestroy, OnInit, PLATFORM_ID } from '@angular/core';
import { SharedService } from '../../shared/services/shared.service';
import { environment } from '../../../environments/environment';
import { ChatService } from '../../services/chat/chat.service';
import { Room, RoomConnectOptions, RemoteTrackPublication, createLocalAudioTrack, RoomEvent, RemoteTrack, RemoteParticipant, isAudioTrack, TrackEvent, ParticipantEvent, RemoteAudioTrack } from 'livekit-client';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { ActivatedRoute, Router, RouterLink, RouterModule } from '@angular/router';
import { AnimationOptions, LottieComponent } from 'ngx-lottie';
import { TranslatePipe } from '../../pipes/translate.pipe';
import { FormsModule } from '@angular/forms';
import { AnimationItem } from 'lottie-web';
import { ThemeService } from '../../shared/theme.service';
import { User } from '../../model/user.model';
import { BarVisualizerComponent } from './bar-visualizer/bar-visualizer.component';

@Component({
    selector: 'app-livekit-voice',
    templateUrl: './voice-liv.component.html',
    imports: [CommonModule, RouterModule, LottieComponent, TranslatePipe, FormsModule, BarVisualizerComponent, RouterLink],
    providers: [TranslatePipe],
    styleUrls: ['./voice-liv.component.scss']
})
export class LivekitVoiceComponent implements OnInit, OnDestroy {
  private liveKitUrl = environment.liveKitUrl; // Change this to your LiveKit server URL
  private token = ''; // Replace with a valid token from your LiveKit server
  private room: Room = new Room({
    adaptiveStream: false, // Improves performance
  });
  isAISpeaking = false;
  isUserSpeaking = false;
  isListening = false;
  transcription = {
    text: '',
    isAgent: false
  };
  userText = '';
  isBrowser = false;
  animation!: AnimationItem;
  listeningOptions: AnimationOptions = {
    path: '/assets/animations/listening.json',
  };
  talkingOptions: AnimationOptions = {
    path: '/assets/animations/New_AI_Talking.json'
  };
  processingOptions: AnimationOptions = {
    path: '/assets/animations/processing-black-new.json',
  };
  isProcessing = false;
  user: User = new User();
  private latitude: number | null = null;
  private longitude: number | null = null;
  connectedToRoom = false;
  connectingToRoom = false;
  listeningAnimation!: AnimationItem;
  speakingAnimation!: AnimationItem;
  agentParticipant: RemoteParticipant | null = null;
  agentStatus = 'idle'; // Default status
  chatId = '';
  public activeAudioTrack!: MediaStreamTrack;
  voiceAssistantState = '';
  leftRoom = false;
  constructor(
    private _sharedService: SharedService,
    private _chatService: ChatService,
    @Inject(PLATFORM_ID) private platformId: object,
    private _themeService: ThemeService,
    private _router: Router,
    private _activatedRoute: ActivatedRoute
  ) {
    effect(() => {
      const location = this._sharedService.gpsSignal();
      if (!location.latitude && !location.longitude) {
        this._chatService.getGps();
      } else {
        this.latitude = location.latitude;
        this.longitude = location.longitude;
      }
    })
  }

  ngOnInit() {
    if (isPlatformBrowser(this.platformId)) {
      this._activatedRoute.queryParams.subscribe(params => {
        if (params['id']) {
          this.chatId = params['id'] ?? this.chatId;
        }
      })
      this.isBrowser = true;
      this._themeService.enableDarkTheme(true);
      
      if (!this.longitude || !this.latitude) {
        this._chatService.getGps();
      }
      this._sharedService.user$.subscribe((user) => {
        if (user.email) {
          this.user = user;
          this._sharedService.loading.set(false);
        }
      });
      this.getLatestChatId();
    }
  }

  getLatestChatId() {
    this._chatService.getLatestChatId().subscribe(res => {
      this.chatId = res.chatId;
    });
  }

  joinCall() {
    this.connectingToRoom = true;
    this.leftRoom = false;
    const latitude = this._sharedService.getLocalStorage('latitude');
    const longitude = this._sharedService.getLocalStorage('longitude');
    this._chatService.getLivkitToken(latitude || '', longitude || '', this.chatId).subscribe(res => {
      this.token = res.token;
      this.chatId = res.chatId;
      this.room.prepareConnection(this.liveKitUrl, this.token);
      this.connectToRoom(this.liveKitUrl, this.token);
    })
  }

  async connectToRoom(url: string, token: string) {
    try {
      this.room = new Room({
        adaptiveStream: false, // Improves performance
      });

      // pre-warm connection, this can be called as early as your page is loaded
      await this.room.connect(url, token, {
        autoSubscribe: true, // Automatically subscribe to audio tracks
      } as RoomConnectOptions);
      
      console.log('✅ Connected to room:', this.room.name);
      // Set metadata for the local participant
      this.room.localParticipant.on(ParticipantEvent.IsSpeakingChanged, (isSpeaking) => {
        // this.isAISpeaking = !message;
        this.isProcessing = !isSpeaking;
        this.isListening = isSpeaking;
        this.isUserSpeaking = isSpeaking;
      });

      // ✅ Publish an audio track so others can hear
      const localAudioTrack = await createLocalAudioTrack();
      // await this.room.localParticipant.setMicrophoneEnabled(true);
      await this.room.localParticipant.publishTrack(localAudioTrack);

      // ✅ Handle new participants joining
      this.room.on(RoomEvent.ParticipantConnected, (participant) => {
        if (participant.identity.includes('agent')) { 
          this.agentParticipant = participant;
          this.connectedToRoom  = true;
          this.connectingToRoom = false;
          this.isListening = true;
        }
      });

      await this._chatService.startTranscription(this.token).subscribe((res: any) => {
        console.log('Transcription started:', res);
      });

      // ✅ Handle when a participant publishes a new track
      this.room.on(RoomEvent.TrackPublished, async (publication: RemoteTrackPublication, participant: RemoteParticipant) => {
        if (publication.kind === 'audio') {
          publication.on(TrackEvent.Subscribed, (track: RemoteTrack) => {
            if (track.kind === 'audio') {
              const audioElement = track.attach();
              document.body.appendChild(audioElement);
              const mediaStreamTrack = (track as RemoteAudioTrack).mediaStreamTrack;
              // Pass this to a variable that your component binds to
              this.activeAudioTrack = mediaStreamTrack;
            }
          });

          publication.on(TrackEvent.Unsubscribed, (track: RemoteTrack) => {
            console.log(`🚫 Unsubscribed from ${participant.identity}`);
            this.leaveCall();
            track.detach().forEach(el => el.remove());
          });
        }
      });

      // this.room.on(RoomEvent.ActiveSpeakersChanged, (Participants) => {
      //   if (Participants.length) {
      //     this.audioLevel = Participants[0].audioLevel;
      //     setTimeout(() => {
      //       this.audioLevel = 0;
      //     }, 2000);
      //   }
      // });

      this.room.on(RoomEvent.ParticipantAttributesChanged, (prevAttributes, participant) => {
        if (participant.identity.includes('agent')) {
          const agentState = participant.attributes['lk.agent.state'];
          this.voiceAssistantState = agentState;
          this.isProcessing = false;
          if (agentState === 'listening') {
            this.isListening = true;
            this.isAISpeaking = false;
          } else if (agentState === 'speaking') {
            this.isListening = false;
            this.isAISpeaking = true;
          }
          this.agentStatus = agentState;
        }
      });


      this.room.on(RoomEvent.TranscriptionReceived, (data, participant) => {
        if (data.length) {
          if (participant?.identity.includes('agent')) {
            this.transcription.isAgent = true;
          } else {
            this.transcription.isAgent = false;
          }
          const maxLen = 60;
          const fullText = data[0].text || '';
          this.transcription.text = fullText.length > maxLen
            ? '...' + fullText.slice(fullText.length - maxLen)
            : fullText; 
        }
      });

      this.room.remoteParticipants.forEach((participant) => {
        participant.trackPublications.forEach((publication) => {
          if (publication.isSubscribed) {
            const track = publication.track;
            if (track && isAudioTrack(track)) {
              const audioElement = track.attach();
              document.body.appendChild(audioElement);
              const mediaStreamTrack = (track as RemoteAudioTrack).mediaStreamTrack;
              // Pass this to a variable that your component binds to
              this.activeAudioTrack = mediaStreamTrack;

              // Optional: still play audio
              
              if (!this.connectedToRoom) {
                this.connectedToRoom  = true;
                this.connectingToRoom = false;
              }
              
            }
          }
        })
      });
    } catch (error) {
      console.error('❌ Error connecting to LiveKit:', error);
    }
  }

  leavePage(){
    this.leaveCall();
    this._router.navigate(['/']);
    // if (this.chatId) {
    //   this._router.navigate(['/voice-response/history'], {
    //     queryParams: { id: this.chatId },
    //   });
    // } else {
    //   this._router.navigate(['/']);
    // }
  }

  muteAudio(){
  }

  leaveCall() {
    this.connectedToRoom = false;
    this.isProcessing = false;
    this.isAISpeaking = false;
    this.isUserSpeaking = false;
    this.isListening = false;
    this.transcription.text = '';
    this.room?.disconnect();
    this.leftRoom = true;
    this.room = new Room({
      adaptiveStream: false, // Improves performance
    });
  }

  ngOnDestroy(): void {
    this.leaveCall();
  }
}
